"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  buildPool,
  type CaseMode,
  drawLetters,
  parseExcluded,
} from "@/lib/tool-kit/random-tools/letters";

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_LETTER_GENERATOR_STRINGS = {
  letterCase: "Letter case",
  caseModes: {
    uppercase: "Uppercase (A–Z)",
    lowercase: "Lowercase (a–z)",
    both: "Both cases",
  },
  howManyLetters: "How many letters (1–100)",
  excludeLetters: "Exclude letters (optional)",
  excludePlaceholder: "e.g. q x z",
  allowRepeats: "Allow repeats",
  generateLetters: "Generate Letters",
  copied: "Copied!",
  copyAll: "Copy All",
  allExcludedError: "Every letter is excluded — remove some exclusions first.",
  noRepeatsError:
    "one:Without repeats, only {count} letter remains in the pool — lower the quantity or allow repeats.|other:Without repeats, only {count} letters remain in the pool — lower the quantity or allow repeats.",
  yourRandomLetters: "Your random letters",
} as const;

const CASE_MODE_VALUES: CaseMode[] = ["uppercase", "lowercase", "both"];

const MIN_COUNT = 1;
const MAX_COUNT = 100;

interface LetterChip {
  id: string;
  value: string;
}

function clampCount(value: number): number {
  if (Number.isNaN(value)) {
    return MIN_COUNT;
  }
  return Math.min(MAX_COUNT, Math.max(MIN_COUNT, Math.floor(value)));
}

export default function RandomLetterGeneratorWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(RANDOM_LETTER_GENERATOR_STRINGS);
  const [caseMode, setCaseMode] = useState<CaseMode>("uppercase");
  const [count, setCount] = useState(5);
  const [allowRepeats, setAllowRepeats] = useState(true);
  const [excludeInput, setExcludeInput] = useState("");
  const [letters, setLetters] = useState<LetterChip[]>([]);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  function handleGenerate() {
    const pool = buildPool(caseMode, parseExcluded(excludeInput));
    if (pool.length === 0) {
      setError(strings.allExcludedError);
      setLetters([]);
      return;
    }
    if (!allowRepeats && count > pool.length) {
      setError(tPlural(strings.noRepeatsError, locale, pool.length));
      setLetters([]);
      return;
    }
    setError("");
    const values = drawLetters(pool, count, allowRepeats);
    setLetters(values.map((value) => ({ id: crypto.randomUUID(), value })));
    setCopied(false);
  }

  function handleCopyAll() {
    if (letters.length === 0) {
      return;
    }
    const text = letters.map((l) => l.value).join(", ");
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Case mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.letterCase}
        </p>
        <div className="flex flex-wrap gap-3">
          {CASE_MODE_VALUES.map((m) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={m}
            >
              <input
                checked={caseMode === m}
                className="accent-primary"
                name="case-mode"
                onChange={() => setCaseMode(m)}
                type="radio"
                value={m}
              />
              {strings.caseModes[m]}
            </label>
          ))}
        </div>
      </section>

      {/* Quantity + options */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="letter-count"
            >
              {strings.howManyLetters}
            </label>
            <input
              className="w-32 rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="letter-count"
              max={MAX_COUNT}
              min={MIN_COUNT}
              onChange={(e) => setCount(clampCount(Number(e.target.value)))}
              type="number"
              value={count}
            />
          </div>
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="letter-exclude"
            >
              {strings.excludeLetters}
            </label>
            <input
              className="w-48 rounded-lg border border-border bg-card p-3 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="letter-exclude"
              onChange={(e) => setExcludeInput(e.target.value)}
              placeholder={strings.excludePlaceholder}
              type="text"
              value={excludeInput}
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={allowRepeats}
              className="accent-primary"
              onChange={(e) => setAllowRepeats(e.target.checked)}
              type="checkbox"
            />
            {strings.allowRepeats}
          </label>
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateLetters}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={letters.length === 0}
          onClick={handleCopyAll}
          type="button"
        >
          {copied ? strings.copied : strings.copyAll}
        </button>
      </div>

      {error.length > 0 && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
          {error}
        </p>
      )}

      {/* Results */}
      {letters.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.yourRandomLetters}
          </h2>
          <div className="flex flex-wrap gap-2">
            {letters.map((l) => (
              <span
                className="rounded-full border border-border bg-card px-4 py-1.5 font-mono text-foreground text-sm"
                key={l.id}
              >
                {l.value}
              </span>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
